import { useState, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/context/AuthContext";
import { useGlobalLoading } from "@/context/GlobalLoadingContext";
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';

const TaskNotificationSubscription = ({ task, onTaskChange, onRefreshActivities, organizationSlug, user, userRole, isLoading = false }) => {
    const { toast } = useToast();
    const { axiosInstance } = useAuth();
    const { showLoading, hideLoading } = useGlobalLoading();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [usersFetched, setUsersFetched] = useState(false);
    const [visibleUsersCount, setVisibleUsersCount] = useState(5);

    // Calculate if current user is subscribed or is the assignee (fake subscribed)
    const isCurrentUserAssignee = task?.assignee?.id === user.id;
    const isCurrentUserSubscribed = task?.subscribedUsers?.some(subbedUser => subbedUser.id === user.id) || isCurrentUserAssignee;

    // Lazy load users when dropdown opens
    useEffect(() => {
        if (dropdownOpen && !usersFetched && !usersLoading) {
            const fetchUsers = async () => {
                setUsersLoading(true);
                try {
                    const params = new URLSearchParams();
                    params.append('organizationSlug', organizationSlug);
                    if (task?.key) {
                        params.append('taskKey', task.key);
                    }
                    const response = await axiosInstance.get(`/users/sorted?${params.toString()}`);
                    setUsers(response.data);
                    setUsersFetched(true);
                } catch (error) {
                    console.error('Error fetching users:', error);
                    toast({
                        title: "Error",
                        description: "Failed to fetch users",
                        variant: "destructive"
                    });
                } finally {
                    setUsersLoading(false);
                }
            };
            fetchUsers();
        }
    }, [dropdownOpen, organizationSlug, task?.key, axiosInstance, toast, usersFetched, usersLoading]);

    // Reset visible count when dropdown closes
    useEffect(() => {
        if (!dropdownOpen) {
            setVisibleUsersCount(5);
        }
    }, [dropdownOpen]);

    const handleToggleSubscriber = async (userId, checked) => {
        // 1. Capture old value for rollback
        const oldSubscribedUsers = task.subscribedUsers || [];
        const userToToggle = users.find(u => u.id === userId);
        
        // 2. Optimistically update UI immediately (checkbox updates instantly)
        let optimisticTask;
        if (checked) {
            // Add user to subscribers
            optimisticTask = {
                ...task,
                subscribedUsers: [...oldSubscribedUsers, {
                    id: userToToggle.id,
                    name: userToToggle.name,
                    initials: userToToggle.initials,
                    email: userToToggle.email,
                    avatarColor: userToToggle.avatarColor
                }]
            };
        } else {
            // Remove user from subscribers
            optimisticTask = {
                ...task,
                subscribedUsers: oldSubscribedUsers.filter(u => u.id !== userId)
            };
        }
        onTaskChange(optimisticTask);
        
        // 3. Fire API call (don't await before UI update)
        try {
            showLoading();
            if (checked) {
                await axiosInstance.post(`/tasks/${organizationSlug}/${task.key}/subscribe`, {
                    userId
                });
            } else {
                await axiosInstance.post(`/tasks/${organizationSlug}/${task.key}/unsubscribe`, {
                    userId
                });
            }
            
            // 4. Refresh just activities (new activity was created)
            if (onRefreshActivities) {
                await onRefreshActivities();
            }
        } catch (error) {
            // 5. Rollback on error
            console.error('Error toggling subscriber:', error);
            onTaskChange({ ...task, subscribedUsers: oldSubscribedUsers });
            toast({
                title: "Error",
                description: checked ? "Failed to add subscriber" : "Failed to remove subscriber",
                variant: "destructive"
            });
        } finally {
            hideLoading();
        }
    };

    // Check if a user is subscribed or is the assignee (fake subscribed)
    const isUserSubscribed = (userId) => {
        const isAssignee = task?.assignee?.id === userId;
        const isSubscribed = task?.subscribedUsers?.some(sub => sub.id === userId) || false;
        return isSubscribed || isAssignee;
    };

    // Check if a user is the assignee (and thus cannot unsubscribe)
    const isUserAssignee = (userId) => {
        return task?.assignee?.id === userId;
    };

    if (isLoading) {
        return (
            <Skeleton className="h-7 w-7 rounded-md bg-muted/60 border border-muted/30 shadow-sm" />
        );
    }

    if (!task) {
        return null;
    }

    return (
        <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 px-2 bg-muted/60 hover:bg-muted/80 shadow-sm border border-muted/30">
                    {isCurrentUserSubscribed ? (
                        <Bell className="w-4 h-4 text-primary" fill="currentColor" strokeWidth={0} />
                    ) : (
                        <Bell className="w-4 h-4 text-primary" />
                    )}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
                {/* Subscribers Section */}
                <div className="px-2 py-1.5">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-muted-foreground">Subscribers</span>
                    </div>
                    
                    {/* Users List with Checkboxes */}
                    <div onClick={(e) => e.stopPropagation()}>
                        {usersLoading ? (
                            <div className="space-y-2 px-2 py-1">
                                <Skeleton className="h-5 w-2/3 rounded" />
                                <Skeleton className="h-5 w-3/4 rounded" />
                                <Skeleton className="h-5 w-1/2 rounded" />
                            </div>
                        ) : users.length > 0 ? (
                            <>
                                <div className="space-y-0.5">
                                    {users.slice(0, visibleUsersCount).map((mappedUser) => {
                                        const isSubscribed = isUserSubscribed(mappedUser.id);
                                        const isAssignee = isUserAssignee(mappedUser.id);
                                        return (
                                            <div
                                                key={mappedUser.id}
                                                className={cn(
                                                    "group flex items-center gap-2 text-sm px-2 py-1.5 rounded-sm",
                                                    isAssignee ? "opacity-60 cursor-not-allowed" : "hover:bg-accent cursor-pointer"
                                                )}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (!isAssignee) {
                                                        handleToggleSubscriber(mappedUser.id, !isSubscribed);
                                                    }
                                                }}
                                            >
                                                <Checkbox
                                                    checked={isSubscribed}
                                                    onCheckedChange={(checked) => {
                                                        if (!isAssignee) {
                                                            handleToggleSubscriber(mappedUser.id, checked);
                                                        }
                                                    }}
                                                    disabled={isAssignee}
                                                    className="flex-shrink-0 border-muted-foreground/60 data-[state=checked]:border-primary group-hover:border-muted-foreground data-[state=checked]:group-hover:border-primary"
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                                <Avatar className="h-5 w-5 flex-shrink-0">
                                                    <AvatarImage src={mappedUser.avatar} />
                                                    <AvatarFallback className={`${mappedUser.avatarColor || 'bg-neutral-600'} text-neutral-200 text-[0.65rem]`}>
                                                        {mappedUser.initials}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <span className="truncate flex-1">
                                                    {mappedUser.name}
                                                </span>
                                                {isAssignee && (
                                                    <span className="text-xs text-muted-foreground ml-1">(Assigned)</span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                                {users.length > visibleUsersCount && (
                                    <div className="pt-2 border-t mt-2">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="w-full text-xs"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setVisibleUsersCount(prev => Math.min(prev + 5, users.length));
                                            }}
                                        >
                                            Load more ({users.length - visibleUsersCount} remaining)
                                        </Button>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="p-4 text-center text-sm text-muted-foreground">
                                No users available
                            </div>
                        )}
                    </div>
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export default TaskNotificationSubscription;

